/**
 * De vier chat-panelen (Waiting queue, Unanswered, Spenders, Timewasters)
 * komen NIET uit vier queries. De backend zegt het zelf, in
 * `admin_conversations.py` rond regel 3198:
 *
 *   "This one clause covers the main grid AND the Unanswered, Waiting,
 *    Spenders and Timewasters panels, because all four are bucketed client
 *    side from exactly these rows."
 *
 * Eén query (`queue_candidates=true&panel=unanswered&limit=350`) levert de
 * rijen; de verdeling gebeurt hier. De predicaten hieronder zijn één op één
 * overgenomen uit de bundle — daar heten ze `She`, `out`, `Hl`, `mw`, `sut`,
 * `lut`, `tT` en `tut`. Ze zijn subtiel: een eerdere poging telde de wachtrij
 * als "onbeantwoord" en kwam op 75 in plaats van 3.
 */

export interface QueueRow {
  id: number
  platform?: string | null
  last_message_sender?: string | null
  /** 03-10-2026: zender van de nieuwste rij die geen Mass DM is. */
  queue_last_sender?: string | null
  fan_messages?: number | null
  last_fan_message_at?: string | null
  last_response_at?: string | null
  last_read_at?: string | null
  queue_fan_message_at?: string | null
  queue_response_at?: string | null
  last_send_failed?: boolean | null
  last_send_failed_at?: string | null
  last_send_error?: string | null
  last_send_is_mass_dm?: boolean | null
  waiting_dismissed_fan_message_at?: string | null
  last_waiting_queue_opened_at?: string | null
  last_dashboard_opened_at?: string | null
  is_timewaster?: boolean | null
  /** 21-10-2026 (Jordy): aparte vlag "Maybe timewaster". */
  is_maybe_timewaster?: boolean | null
  timewaster_marked_at?: string | null
  fan_total_spend?: number | null
  /** 19-10-2026: zelfde fan over alle modellen (bestaande koppelingen). */
  fan_total_spend_all_models?: number | null
  /** 16-10-2026: het nieuwste fanbericht is een tip ("[Tip - N credits]"). */
  queue_last_fan_is_tip?: boolean | null
  /** 18-10-2026: multi-chatter inbox (src/services/inbox_claim.py). */
  inbox_claim_admin_id?: number | null
  inbox_claim_name?: string | null
  inbox_claim_at?: string | null
  inbox_claim_until?: string | null
  inbox_claim_active?: boolean | null
  /** "waiting" (wacht op de fan), "replied" (5 min-aftelling), "released" (verlopen; alleen Waiting). */
  inbox_claim_state?: 'open' | 'waiting' | 'replied' | 'released' | 'expired' | null
  inbox_claim_mine?: boolean | null
  inbox_open_admin_id?: number | null
  inbox_open_name?: string | null
  inbox_open_mine?: boolean | null
  /** 20-10-2026: nieuwe volger (welkomstbericht, nog geen reactie); src/services/nieuwe_volgers.py. */
  nieuwe_volger?: boolean | null
  nieuwe_volger_op?: string | null
  /** 23-10-2026: nieuwe betaalde abonnee, nog niet persoonlijk bedankt (max 24 uur);
   *  src/services/nieuwe_abonnee.py. */
  new_paid_subscriber_at?: string | null
  new_paid_subscriber_amount?: number | null
  [k: string]: unknown
}

/** 24-10-2026: besteding over alle modellen, nooit minder dan dit model
 *  (zelfde regel als lib/spendAlleModellen.ts `alleModellen`). */
export const bestedingAlleModellen = (c: QueueRow): number =>
  Math.max(Number(c?.fan_total_spend || 0), Number(c?.fan_total_spend_all_models || 0))

const ms = (v: unknown): number => {
  if (!v) return 0
  const t = new Date(String(v)).getTime()
  return Number.isFinite(t) ? t : 0
}

/**
 * bundle `She` — de laatste zender is de fan.
 *
 * 03-10-2026 (Jordy — "Only a personal message counts as an answer"):
 * `last_message_sender` is de nieuwste opgeslagen rij, en na een Mass DM is
 * dat de kopie zelf. De backend levert `queue_last_sender`: de zender van de
 * nieuwste rij die geen Mass DM is. Die gaat voor; zonder dat veld (oudere
 * backend, of een gesprek met alleen Mass DM's) geldt de oude regel.
 */
export const lastSenderIsFan = (c: QueueRow): boolean =>
  String(c?.queue_last_sender || c?.last_message_sender || '').toLowerCase() === 'fan'

/**
 * bundle `tT` — de laatste uitgaande poging is mislukt.
 *
 * 03-10-2026 (Jordy): een mislukte Mass DM-kopie telt niet. Eerder haalde die
 * een wachtende fan helemaal uit de wachtrij (en verschoof zijn wachttijd naar
 * het moment van falen); nu blijft de fan staan zoals vóór de Mass DM.
 */
export const sendFailed = (c: QueueRow): boolean =>
  !!c?.last_send_failed && !!c?.last_send_failed_at && !c?.last_send_is_mass_dm

/** bundle `tut` — een foutmelding die betekent dat de chat dood is. Zo'n rij
 *  hoort niet in de wachtrij: er valt niets meer te antwoorden. */
export const deadChatError = (err: unknown): boolean => {
  const t = String(err || '').toLowerCase()
  if (!t) return false
  return (
    t.includes('unfollow') ||
    t.includes("can't send messages in this chat") ||
    t.includes('cannot send messages in this chat') ||
    t.includes('no chatuser matches') ||
    t.includes('cannot find this fan') ||
    t.includes('fan has unfollowed') ||
    t.includes('chat is closed')
  )
}

/** bundle `Hl` — het tijdstip waarop de wachtrij sorteert. Bij een mislukte
 *  verzending telt het moment van falen mee, want dáár moet iemand op af. */
export const queueStamp = (c: QueueRow): string | null => {
  const base = Object.prototype.hasOwnProperty.call(c ?? {}, 'queue_fan_message_at')
    ? c.queue_fan_message_at || null
    : c?.last_fan_message_at || null
  if (!sendFailed(c)) return base
  const failed = c?.last_send_failed_at || null
  if (!base) return failed
  const b = ms(base), f = ms(failed)
  if (!f) return base
  return f >= b ? failed : base
}

const queueResponse = (c: QueueRow): string | null =>
  Object.prototype.hasOwnProperty.call(c ?? {}, 'queue_response_at')
    ? c.queue_response_at || null
    : c?.last_response_at || null

/** bundle `out` — Unanswered messages. */
/**
 * Niet meer in gebruik voor het paneel: oud leidt Unanswered af uit de
 * drieweg-splitsing in `bucketPanels`, niet uit een eigen predicaat. Blijft
 * staan omdat het het `out`-predicaat uit de oude bundle exact weergeeft.
 */
export const isUnanswered = (c: QueueRow): boolean => {
  if (!c || !lastSenderIsFan(c)) return false
  if (Number(c.fan_messages || 0) <= 0) return false
  if (!c.last_fan_message_at) return false
  const n = ms(c.last_fan_message_at)
  if (!n) return false
  const seen = Math.max(ms(c.last_response_at), ms(c.last_read_at))
  return seen === 0 || n > seen
}

/**
 * bundle `mw` — Waiting queue.
 *
 * `dismissedBefore` is het moment waarop de gebruiker de wachtrij heeft
 * leeggeklikt; alles van vóór dat moment valt weg.
 */
/**
 * 23-10-2026 (Jordy): een nieuwe betaalde abonnee die nog geen persoonlijk bericht
 * kreeg (de server zet het veld alleen binnen 24 uur na de betaling en tot een
 * chatter iets stuurt; een Mass DM of het welkomstbericht telt niet).
 */
export const nieuweAbonnee = (c: QueueRow): boolean => !!c?.new_paid_subscriber_at

export const isWaiting = (c: QueueRow, dismissedBefore?: string | null): boolean => {
  if (!c) return false
  // 23-10-2026 (Jordy): zo'n fan wacht, ook als hij niets schreef en het laatste
  // bericht van ons is (welkomstbericht). Wegklikken blijft werken.
  if (nieuweAbonnee(c)) {
    const op = ms(c.new_paid_subscriber_at)
    if (op > Math.max(ms(c.waiting_dismissed_fan_message_at), ms(dismissedBefore))) return true
  }
  const failed = sendFailed(c)
  if (failed && deadChatError(c.last_send_error)) return false
  if (!failed && !lastSenderIsFan(c)) return false
  if (
    !failed &&
    String(c.platform || '').toLowerCase() === 'fancentro' &&
    Number(c.fan_messages || 0) <= 0
  ) return false

  const stamp = queueStamp(c)
  if (!stamp) return false
  const a = ms(stamp)
  if (!a) return false

  const responded = failed ? 0 : ms(queueResponse(c))
  const dismissed = Math.max(
    ms(c.waiting_dismissed_fan_message_at),
    ms(dismissedBefore),
  )
  return a > responded && a > dismissed
}

/**
 * Timewasters: gemarkeerd en de fan wacht op antwoord.
 *
 * 27-09 (Jordy — "als timewaster gemarkeerd, maar het verschijnt niet in de
 * Timewasters-widget"). Oud (`sut`) toonde een gemarkeerde chat pas als de
 * fan NA het markeren opnieuw schreef. Wie markeert terwijl de fan al wacht
 * — het gewone geval — zag de chat daardoor nergens meer: niet hier, en
 * bucketPanels haalt timewasters ook uit de wachtrij. Nu staat elke
 * gemarkeerde, wachtende chat hier, ongeacht wanneer de fan schreef.
 */
export const isTimewaster = (c: QueueRow, dismissedBefore?: string | null): boolean => {
  if (!c?.is_timewaster) return false
  return isWaiting(c, dismissedBefore)
}

/**
 * 21-10-2026 (Jordy): Maybe timewasters: gemarkeerd als "maybe" en de fan wacht
 * op antwoord. Een ECHTE timewaster staat nergens meer op de hoofdpagina (alleen
 * onder het All chats-filter), een maybe-timewaster alleen in dit paneel.
 */
export const isMaybeTimewaster = (c: QueueRow, dismissedBefore?: string | null): boolean => {
  if (!c?.is_maybe_timewaster || c.is_timewaster) return false
  return isWaiting(c, dismissedBefore)
}

/** 21-10-2026 (Jordy): hoort niet in de gewone panelen (timewaster of maybe). */
export const isGemarkeerd = (c: QueueRow): boolean => !!(c?.is_timewaster || c?.is_maybe_timewaster)

/** bundle `lut` — al geopend, dus uit de wachtrij weg. */
export const alreadyOpened = (c: QueueRow): boolean => {
  const stamp = queueStamp(c)
  if (!stamp || !c) return false
  const n = ms(stamp)
  const r = Math.max(ms(c.last_waiting_queue_opened_at), ms(c.last_dashboard_opened_at))
  return !!n && !!r && r >= n
}

/** Spenders: wachtende chats van fans die al iets hebben uitgegeven. */
export const isSpender = (c: QueueRow, dismissedBefore?: string | null): boolean =>
  Number(c?.fan_total_spend || 0) > 0 && isWaiting(c, dismissedBefore)

/**
 * 18-10-2026 (Jordy): multi-chatter inbox, definitieve regel (src/services/inbox_claim.py).
 * Eigenaar = wie het laatst persoonlijk antwoordde, mits nog ingelogd.
 *   "waiting"  geen fanbericht erna: van de eigenaar zolang hij actief is (19-10-2026:
 *              geen shiftgrens meer; kwijt bij uitloggen/losmaken of 1 uur geen verstuurd bericht);
 *   "replied"  fan antwoordde: 5 min vanaf het oudste onbeantwoorde fanbericht;
 *   "released" die 5 min zijn om: alleen nog Waiting (groen) voor iedereen.
 * 18-10-2026 (Jordy, derde ronde): "released" staat NIET meer in My chats; de
 * 5-min-regel en het verlies van het eigenaarschap (uitloggen/losmaken/1 uur stil)
 * geven allebei vrij, wat het eerst komt.
 */
export const claimActief = (c: QueueRow): boolean => !!c?.inbox_claim_active

/** Nu van een ander account: hoort in geen enkel wachtrijpaneel van mij. */
export const claimVanEenAnder = (c: QueueRow): boolean => claimActief(c) && !c?.inbox_claim_mine

/** De 5 minuten na het fan-antwoord zijn om en niemand antwoordde. */
export const vrijgevallenOnbeantwoord = (c: QueueRow): boolean =>
  c?.inbox_claim_state === 'released' && !!c?.inbox_claim_admin_id

/**
 * Het groene "Unanswered"-ballonnetje (voor iedereen gelijk): de fan wacht,
 * niemand heeft het gesprek nu, en het is al geopend of was net van iemand.
 */
export const isGroen = (c: QueueRow, dismissedBefore?: string | null): boolean =>
  isWaiting(c, dismissedBefore) && !claimActief(c)
    && (alreadyOpened(c) || wachtNaClaim(c) || isAbonnementMarker(c))

/**
 * 18-10-2026 (Jordy): de fan wacht na een claim die voorbij is: "released" (5 min om,
 * eigenaar nog actief) of "expired" (eigenaarschap kwijt: uitloggen/losmaken/1 uur stil;
 * niet meer in My chats). Beide: groen in Waiting, ook bij een spender.
 */
export const wachtNaClaim = (c: QueueRow): boolean =>
  vrijgevallenOnbeantwoord(c) || c?.inbox_claim_state === 'expired'

/**
 * 18-10-2026 (Jordy): het nieuwste bericht is "[New subscription – €X]" of
 * "[Renewed subscription – €X]" (src/services/abonnement_bericht.py): groen in
 * Waiting, zodat een chatter de fan persoonlijk bedankt.
 */
export const isAbonnementMarker = (c: QueueRow): boolean =>
  /^\[(New|Renewed) subscription – €/.test(String(c?.last_message ?? ''))

/**
 * Hoort in My chats. Chatter: alleen de eigen LOPENDE claims ("waiting" of
 * "replied"). Owner/admin (`allesZien`): de lopende claims van iedereen.
 * 18-10-2026 (Jordy): vrijgevallen ("released", 5 min om) is weg uit My chats en
 * staat alleen nog groen in Waiting, net als na verlies van het eigenaarschap.
 */
/**
 * 26-10-2026 (Jordy): het eindmoment (ms) van een lopende 5-minutenaftelling in My chats:
 * de claim is actief, de fan antwoordde ("replied") en er is een eindtijd. Anders null.
 * Dezelfde voorwaarde als de aftelbadge in ChatPanels (InboxBadges).
 */
export const aftelTot = (c: QueueRow): number | null => {
  if (!claimActief(c) || c?.inbox_claim_state !== 'replied') return null
  const t = ms(c.inbox_claim_until)
  return t || null
}

export const inMijnBox = (c: QueueRow, _dismissedBefore?: string | null, allesZien = false): boolean => {
  if (!c?.inbox_claim_admin_id || !c.inbox_claim_state) return false
  if (!allesZien && !c.inbox_claim_mine) return false
  return claimActief(c)
}

/**
 * De emmers in één keer, uit dezelfde rijen.
 *
 * 18-10-2026 (Jordy): Waiting en Unanswered zijn SAMEN één Waiting queue:
 * bovenaan de groene (geopend of net vrijgevallen, nog geen antwoord), daarna
 * de rest; binnen elke groep de oudste eerst. Hetzelfde in Spenders.
 * `mine` is de eigen box (de plek van het oude Unanswered): alleen mijn
 * lopende claims (vrijgevallen staat alleen in de globale Waiting, groen).
 * Een gesprek dat NU van een ander is,
 * staat nergens (de server laat het voor chatters al weg).
 *
 * Een spender staat onder Spenders en niet ook in Waiting (behalve na een
 * tip). 21-10-2026: `timewasters` is nu het paneel Maybe timewasters; echte
 * timewasters vallen overal buiten.
 */
export function bucketPanels(rows: QueueRow[], dismissedBefore?: string | null, allesZien = false) {
  const waiting: QueueRow[] = []
  const spenders: QueueRow[] = []
  const mine: QueueRow[] = []
  const timewasters: QueueRow[] = []
  for (const c of rows ?? []) {
    // Oud laat een chat die op het dashboard geblokkeerd is overal buiten.
    if (c?.dashboard_blocked) continue
    // 21-10-2026 (Jordy): een echte timewaster staat in GEEN paneel meer; een
    // maybe-timewaster alleen in "Maybe timewasters" (als hij wacht).
    if (c?.is_timewaster) continue
    if (isMaybeTimewaster(c, dismissedBefore)) { timewasters.push(c); continue }
    if (c?.is_maybe_timewaster) continue
    // Owner/admin zien in My chats de claims van iedereen (met de naam erbij).
    if (inMijnBox(c, dismissedBefore, allesZien)) mine.push(c)
    if (claimVanEenAnder(c)) continue
    // Nu van mij: alleen in mijn box, niet in de globale wachtrij.
    if (claimActief(c)) continue
    if (!isWaiting(c, dismissedBefore)) continue
    // 24-10-2026 (Jordy, optie A): een spender is wie bij WELK model dan ook betaalde
    // (zelfde regel als de VIP-badge, 19-10); eerst telde alleen dit model.
    if (bestedingAlleModellen(c) > 0) {
      spenders.push(c)
      // 16-10-2026 (Jordy): een tip uit het niets moet een chatter laten
      // reageren: zo'n gesprek staat óók in Waiting (en blijft een spender).
      // 18-10-2026: hetzelfde voor een nieuwe/verlengde subscription (de vlag
      // queue_last_fan_is_tip dekt beide).
      // 18-10-2026 (Jordy): een vrijgevallen gesprek (5 min om, geen antwoord)
      // hoort voor IEDEREEN in Waiting, ook als de fan een spender is.
      // 23-10-2026 (Jordy): en een nieuwe betaalde abonnee, zodat iemand hem bedankt.
      if (c.queue_last_fan_is_tip || wachtNaClaim(c) || nieuweAbonnee(c)) waiting.push(c)
    }
    else waiting.push(c)
  }
  // Oudste bovenaan, zoals oud: wie het langst wacht moet eerst geholpen
  // worden. 18-10-2026: groen gaat voor.
  const bySt = (a: QueueRow, b: QueueRow) => ms(queueStamp(a)) - ms(queueStamp(b))
  const groenEerst = (a: QueueRow, b: QueueRow) =>
    Number(isGroen(b, dismissedBefore)) - Number(isGroen(a, dismissedBefore)) || bySt(a, b)
  // 21-10-2026 (Jordy): My chats op het LAATSTE bericht in het gesprek, nieuwste
  // bovenaan, ongeacht wie het stuurde (fan of chatter; geen Mass DM). Vervangt de
  // volgorde "aftellend eerst" van 18-10: een aftellend gesprek heeft een vers
  // fanbericht en staat daardoor vanzelf hoog.
  const laatsteBericht = (c: QueueRow) => Math.max(ms(queueStamp(c)), ms(queueResponse(c)))
  const nieuwsteEerst = (a: QueueRow, b: QueueRow) =>
    laatsteBericht(b) - laatsteBericht(a) || Number(b.id) - Number(a.id)
  return {
    // 20-10-2026 (Jordy): de Waiting queue in één volgorde op tijd.
    // De groene "Unanswered"-badge blijft als markering, maar bepaalt de volgorde niet meer.
    // 21-10-2026 (Jordy): omgedraaid: nieuwste bovenaan, de langst wachtende onderaan.
    // 26-10-2026 (Jordy): weer het langst wachtende gesprek bovenaan, de nieuwste onderaan.
    waiting: waiting.sort(bySt),
    // 24-10-2026 (Jordy): hoogste totale besteding (alle modellen) bovenaan.
    spenders: spenders.sort((a, b) => bestedingAlleModellen(b) - bestedingAlleModellen(a) || groenEerst(a, b)),
    // 26-10-2026 (Jordy): een lopende aftelling (de fan antwoordde; 5 min) staat
    // ALTIJD bovenaan, de minste resterende tijd eerst; daaronder de rest zoals op 21-10.
    mine: mine.sort((a, b) => {
      const ta = aftelTot(a), tb = aftelTot(b)
      if (ta !== null || tb !== null) {
        if (ta === null) return 1
        if (tb === null) return -1
        if (ta !== tb) return ta - tb
      }
      return nieuwsteEerst(a, b)
    }),
    timewasters: timewasters.sort(bySt),
    /**
     * De kopbalk telt álles wat open staat: wachtrij, spenders en mijn
     * gesprekken waarin de fan wacht. Een gesprek in twee emmers telt één keer.
     */
    open: [...new Map([...waiting, ...spenders, ...mine.filter((c) => isWaiting(c, dismissedBefore))]
      .map((c) => [c.id, c])).values()].sort(groenEerst),
  }
}

/**
 * 20-10-2026 (Jordy): nieuwe volgers in de Waiting queue. Een fan die het
 * welkomstbericht van het platform kreeg en nooit reageerde (eigen query,
 * `nieuwe_volgers=true`, eigen limiet van 100). Sluit `isWaiting` uit: zodra de
 * fan schrijft, is hij een gewone wachtende fan en geen nieuwe volger meer.
 */
export const isNieuweVolger = (c: QueueRow): boolean => {
  if (!c?.nieuwe_volger || c.dashboard_blocked || c.is_timewaster || c.is_maybe_timewaster) return false
  if (Number(c.fan_messages || 0) > 0 || c.last_fan_message_at) return false
  if (isWaiting(c)) return false
  const op = ms(c.nieuwe_volger_op)
  return !!op && op > ms(c.waiting_dismissed_fan_message_at)
}

/** De nieuwe volgers die niet al ergens in de wachtrij-rijen staan; nieuwste welkomstbericht eerst. */
export function nieuweVolgers(rows: QueueRow[], wachtrij: QueueRow[] = []): QueueRow[] {
  const al = new Set((wachtrij ?? []).map((c) => c?.id))
  return (rows ?? [])
    .filter((c) => isNieuweVolger(c) && !al.has(c.id))
    .sort((a, b) => ms(b.nieuwe_volger_op) - ms(a.nieuwe_volger_op))
}

/**
 * 21-10-2026 (Jordy): VIP = handmatig VIP-vinkje OF de fan gaf iets uit (totaal over
 * alle modellen > 0: abonnement, unlock of tip). Live, er is geen opgeslagen vlag
 * voor de spend-kant.
 */
export const isVip = (c: QueueRow): boolean =>
  !!c?.is_vip || Number(c?.fan_total_spend_all_models ?? c?.fan_total_spend ?? 0) > 0

/**
 * 21-10-2026 (Jordy): de VIP-lijst blijft staan, ook als wij als laatst schreven.
 * Wie op antwoord wacht staat bovenaan (oudste eerst), daaronder de rest in de
 * volgorde van de server (laatste activiteit). Echte en maybe-timewasters niet.
 */
export function vipLijst(rows: QueueRow[]): QueueRow[] {
  const vips = (rows ?? []).filter((c) => c && !c.dashboard_blocked && !isGemarkeerd(c) && isVip(c))
  const wacht = vips.filter((c) => isWaiting(c)).sort((a, b) => ms(queueStamp(a)) - ms(queueStamp(b)))
  const rest = vips.filter((c) => !isWaiting(c))
  return [...wacht, ...rest]
}
